








































































































import { PartnerPracticeApi } from '@/api/partner-practice';
import loading from '@/components/loading.vue';
import addPartnerPracticeDialog from '@/components/settings/partner-practices/add-partner-practice-dialog.vue';
import editPartnerPracticeDialog from '@/components/settings/partner-practices/edit-partner-practice-dialog.vue';
import partnerPracticesTable from '@/components/settings/partner-practices/partner-practices-table.vue';
import { PartnerPractice } from '@/shared/partner-practice';
import { Component, Vue } from 'vue-property-decorator';
import { namespace } from 'vuex-class';

const userModule = namespace('user');
const partnerPracticeApi = new PartnerPracticeApi();

@Component({
    components: {
        loading,
        addPartnerPracticeDialog,
        editPartnerPracticeDialog,
        partnerPracticesTable,
    },
})
export default class PartnerPractices extends Vue {
    @userModule.Getter('isAdministrator') private isAdministrator!: boolean;

    private isLoading: boolean = true;
    private currentPage: number = 1;
    private pageSize: number = 25;
    private total: number = 0;
    private partnerPractices: Array<PartnerPractice> = new Array<PartnerPractice>();
    private selectedPartnerPractice: PartnerPractice | null = null;
    private nameFilter: string | null = null;
    private statusFilter: number = 1;
    private addPartnerPracticeDialogVisible: boolean = false;
    private editPartnerPracticeDialogVisible: boolean = false;
    private restoreConfirmationDialogVisible: boolean = false;
    private archiveConfirmationDialogVisible: boolean = false;
    private statuses: Array<{ id: number, value: string }> = [
        { id: 0, value: 'All' },
        { id: 1, value: 'Active' },
        { id: 2, value: 'Archived' },
    ];

    private async fetchPartnerPracticesAsync(): Promise<void> {
        this.isLoading = true;

        try {
            const result = await partnerPracticeApi.getPartnerPracticesAsync({
                page: this.currentPage,
                pageSize: this.pageSize,
                name: this.nameFilter,
                status: this.statusFilter,
            });

            this.partnerPractices = result.partnerPractices;
            this.total = result.total;
        } catch (e) {
            if (e instanceof Error) {
                this.$snackbar.open({ message: e.message, type: 'is-danger' });
            } else {
                this.$snackbar.open({ message: 'Could not load partner practices.', type: 'is-danger' });
            }
        }

        this.isLoading = false;
    }

    private async onPageChangedAsync(page: number): Promise<void> {
        this.currentPage = page;
        await this.fetchPartnerPracticesAsync();
    }

    private async onPartnerPracticeCreatedAsync(partnerPractice: PartnerPractice): Promise<void> {
        this.$snackbar.open({ message: `Partner practice '${partnerPractice.name}' has been created.`, type: 'is-success' });

        this.onCancel();
        await this.fetchPartnerPracticesAsync();
    }

    private async onPartnerPracticeUpdatedAsync(partnerPractice: PartnerPractice): Promise<void> {
        this.$snackbar.open({ message: `Partner practice '${partnerPractice.name}' has been updated.`, type: 'is-success' });

        this.onCancel();
        await this.fetchPartnerPracticesAsync();
    }

    private async onRestorePartnerPracticeConfirmedAsync(): Promise<void> {
        try {
            await partnerPracticeApi.updatePartnerPracticeAsync({
                ...this.selectedPartnerPractice!,
                id: this.selectedPartnerPractice!.id!,
                addresses: [...this.selectedPartnerPractice!.addresses],
                emailAttachments: [...this.selectedPartnerPractice!.emailAttachments],
                originalSourceTypes: [...this.selectedPartnerPractice!.originalSourceTypes.map((type) => type)],
                isArchived: false,
                version: this.selectedPartnerPractice!.version!,
            });

            this.$snackbar.open({ message: `Partner practice '${this.selectedPartnerPractice!.name}' has been restored.`, type: 'is-success' });
        } catch (e) {
            if (e instanceof Error) {
                this.$snackbar.open({ message: e.message, type: 'is-danger' });
            } else {
                this.$snackbar.open({ message: `Could not restore partner practice '${this.selectedPartnerPractice!.name}'.`, type: 'is-danger' });
            }
        }

        this.onCancel();
        await this.fetchPartnerPracticesAsync();
    }

    private async onArchivePartnerPracticeConfirmedAsync(): Promise<void> {
        try {
            await partnerPracticeApi.deletePartnerPracticeAsync(this.selectedPartnerPractice!);
            this.$snackbar.open({ message: `Partner practice '${this.selectedPartnerPractice!.name}' has been archived.`, type: 'is-success' });
        } catch (e) {
            if (e instanceof Error) {
                this.$snackbar.open({ message: e.message, type: 'is-danger' });
            } else {
                this.$snackbar.open({ message: `Could not archive partner practice '${this.selectedPartnerPractice!.name}'.`, type: 'is-danger' });
            }
        }

        this.onCancel();
        await this.fetchPartnerPracticesAsync();
    }

    private onAddPartnerPractice(): void {
        this.selectedPartnerPractice = null;
        this.addPartnerPracticeDialogVisible = true;
    }

    private onEditPartnerPractice(partnerPractice: PartnerPractice): void {
        this.selectedPartnerPractice = { ...partnerPractice };
        this.editPartnerPracticeDialogVisible = true;
    }

    private onRestorePartnerPractice(partnerPractice: PartnerPractice): void {
        this.selectedPartnerPractice = { ...partnerPractice };
        this.restoreConfirmationDialogVisible = true;
    }

    private onArchivePartnerPractice(partnerPractice: PartnerPractice): void {
        this.selectedPartnerPractice = { ...partnerPractice };
        this.archiveConfirmationDialogVisible = true;
    }

    private onCancel(): void {
        this.addPartnerPracticeDialogVisible = false;
        this.editPartnerPracticeDialogVisible = false;
        this.restoreConfirmationDialogVisible = false;
        this.archiveConfirmationDialogVisible = false;

        this.selectedPartnerPractice = null;
    }

    private clearFilters(): void {
        this.nameFilter = null;
        this.statusFilter = 1;
    }

    private async mounted(): Promise<void> {
        this.isLoading = true;

        const store = [
            this.$store.dispatch(`types/loadTypesAsync`),
            this.$store.dispatch(`users/loadRolesAsync`),
        ];

        await Promise.all(store);

        await this.fetchPartnerPracticesAsync();

        this.isLoading = false;
    }
}
